import React, { FC, ReactElement } from 'react'
import { graphql, useFragment } from 'react-relay'
import { View } from 'tamagui'
import { Image } from '../Image'

import { UserDefaultPhoto_user$key } from './__generated__/UserDefaultPhoto_user.graphql'

export const UserDefaultPhoto: FC<{
  size?: number
  /** @deprecated No longer needed - signedUrl contains the full URL */
  stableId?: string
  user?: UserDefaultPhoto_user$key
  /** Pre-signed URL from GraphQL for the default avatar. Required when user is not provided. */
  signedUrl?: string
}> = ({ size = 10, user, signedUrl }) => {
  if (user) {
    return (
      <GravatarUserDefaultPhoto
        size={size}
        user={user}
        fallback={<RelayUserDefaultPhoto user={user} size={size} />}
      />
    )
  }

  // signedUrl is required when stableId is provided (guest presenters)
  if (!signedUrl) {
    if (process.env.NODE_ENV !== 'production') {
      console.error(
        'UserDefaultPhoto: signedUrl is required when stableId is provided. ' +
          'Add guestPresenterAvatarUrl to your GraphQL query.'
      )
    }
    return null
  }

  return (
    <Image
      size={size}
      source={signedUrl}
      variant={
        size >= 10 ? 'userProfilePictureLarge' : 'userProfilePictureSmall'
      }
    />
  )
}

const fragment = graphql`
  fragment UserDefaultPhoto_user on User {
    rowId
    emailMd5
    # Signed URL for default avatar (when no profile photo)
    legacyDefaultAvatarUrl: defaultAvatarUrl(
      transform: { w: 48, h: 48, dpr: 3, fo: "auto" }
    )
  }
`
const GravatarUserDefaultPhoto: FC<{
  size?: number
  user: UserDefaultPhoto_user$key
  fallback: ReactElement
}> = ({ user, size = 10, fallback }) => {
  const data = useFragment(fragment, user)

  if (!data.emailMd5) return fallback

  const variant =
    size >= 10 ? 'userProfilePictureLarge' : 'userProfilePictureSmall'

  // Overlay Gravatar (d=blank) on top of default avatar.
  // d=blank returns a transparent image (200) instead of 404 when no Gravatar exists,
  // so the default avatar shows through. If the user has a Gravatar, it covers the default.
  return (
    <View position="relative">
      {fallback}
      <View position="absolute" top={0} left={0}>
        <Image
          size={size}
          source={`https://gravatar.com/avatar/${data.emailMd5}?d=blank&s=${(size || 10) * 3}`}
          variant={variant}
        />
      </View>
    </View>
  )
}

const RelayUserDefaultPhoto: FC<{
  user: UserDefaultPhoto_user$key
  size?: number
}> = ({ user, size = 10 }) => {
  const data = useFragment(fragment, user)

  return (
    <Image
      size={size}
      source={data.legacyDefaultAvatarUrl}
      variant={
        size >= 10 ? 'userProfilePictureLarge' : 'userProfilePictureSmall'
      }
    />
  )
}
